import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "styled-system/css";
import {
  closeButtonStyles,
  dialogStyles,
  headerStyles,
  overlayStyles,
  titleStyles,
} from "./modal.styles";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function Modal({ open, onClose, title, children, className }: ModalProps) {
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onKeyDown]);

  if (!open) return null;

  return createPortal(
    <div
      className={overlayStyles}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cx(dialogStyles, className)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={headerStyles}>
          {title && <h2 className={titleStyles}>{title}</h2>}
          <button type="button" className={closeButtonStyles} aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
